import React, { useMemo, useState } from 'react';
import {
  components, PlaceholderProps, GroupBase, ValueContainerProps, MultiValue,
  MultiValueProps,
  ActionMeta,
  InputActionMeta,
} from 'react-select';
import AsyncSelect from 'react-select/async';
import CustomOption from '../../components/form-inputs/customOption';
import { FrontendFilterValue } from '../../types/filters.type';
import Chip from '../../components/chip';
import { reactElementIsType } from '../../utils/helpers';
import { ReactSelectAction } from '../../types/enums';
import { DelimiterRules, PasteValueHandler } from '../../utils/pasteValueHandler';
import { KeyCode } from '../../utils/constants';

const { ValueContainer, Placeholder } = components;

export interface InputSearchProps {
  searchOptions: MultiValue<FrontendFilterValue>,
  selectedValues: FrontendFilterValue[],
  onChange: (values: FrontendFilterValue[]) => void,
  onUnmatchedPaste?: (unmatched: string[]) => void,
  label: string,
  labelId: string,
  placeholder: string,
  placement: 'top' | 'auto' | 'bottom',
  delimiterRules: DelimiterRules
}

interface CustomValueContainerProps extends ValueContainerProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>> {
  children: React.ReactNode;
}

// creates custom value container that displays number of selected items
function CustomValueContainer({ children, ...props }: Readonly<CustomValueContainerProps>) {
  const { selectProps, hasValue } = props;
  const { inputValue, placeholder } = selectProps;

  const selectedCount = Array.isArray(selectProps.value) ? selectProps.value.length : 0;
  const showCustomPlaceholder = selectedCount > 5 && !inputValue;

  return (
    <ValueContainer {...props}>
      {showCustomPlaceholder && (
        <Placeholder {...props as PlaceholderProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>>}>
          {`${selectedCount} item(s) selected`}
        </Placeholder>
      )}

      {/* Show original placeholder when no selections and no input */}
      {!hasValue && !inputValue && (
        <Placeholder {...props as PlaceholderProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>>} className="text-xs">
          {placeholder}
        </Placeholder>
      )}

      {React.Children.map(children, (child) => (!reactElementIsType(child, Placeholder) ? child : null))}
    </ValueContainer>
  );
}
function CustomMultiValue(props: MultiValueProps<FrontendFilterValue, true, GroupBase<FrontendFilterValue>>) {
  const { selectProps, data, removeProps } = props;
  const selectedCount = Array.isArray(selectProps.value) ? selectProps.value.length : 0;

  if (selectedCount <= 5) {
    return <Chip closeButton text={data.displayName} colour="green" onClick={removeProps.onClick} small />;
  }
  return null;
}

export default function InputSearch({
  searchOptions, selectedValues, onChange, onUnmatchedPaste, label, labelId, placeholder, placement, delimiterRules
}: Readonly<InputSearchProps>) {
  const [inputText, setInputText] = useState('');
  const selectedOptions = useMemo(
    () => (Array.isArray(selectedValues)
      ? searchOptions.filter((option: FrontendFilterValue) => selectedValues?.some((value) => value.displayName === option.displayName))
      : []),
    [searchOptions, selectedValues],
  );

  const pasteHandler = new PasteValueHandler(delimiterRules);
  const truncateOptions = (inputValue: string): FrontendFilterValue[] => {
    if (!inputValue) return searchOptions.slice(0, 50);
    return searchOptions.filter((option) => option.displayName.toLowerCase().includes(inputValue.toLowerCase())).slice(0, 100);
  };

  const loadOptions = (inputValue: string, callback: (options: FrontendFilterValue[]) => void) => {
    setTimeout(() => {
      const filteredOptions = truncateOptions(inputValue);
      callback(filteredOptions);
    }, 300);
  };

  const onInputChange = (newValue: string, action: InputActionMeta) => {
    const options: FrontendFilterValue[] = searchOptions.map((option) => option);
    if (action.action === ReactSelectAction.InputChange) {
      if (pasteHandler.isPastedValue(newValue)) {
        const pastedValues = pasteHandler.parseAndMatchFilterValue(newValue, options);
        if (pastedValues.unMatched.length > 0) {
          onUnmatchedPaste?.(pastedValues.unMatched);
        }
        if (pastedValues.matched.length > 0) {
          onChange?.([...selectedValues, ...pastedValues.matched]);
        }
        setInputText('');
        // Tell react-select the search text is now empty, so it shows the full option list
        return '';
      }
      setInputText(newValue);
    }
    return newValue;
  };

  const handleChange = (
    selectedOptionsProps: MultiValue<FrontendFilterValue>,
    action: ActionMeta<FrontendFilterValue>,
  ) => {
    if (action.action === ReactSelectAction.Clear || action.action === ReactSelectAction.SelectOption) {
      setInputText('');
    }
    const values = selectedOptionsProps ? selectedOptionsProps.map((option) => option) : [];
    onChange(values);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // React Select treats spaces as end-of-input, so we have to disable that behaviour for multi-word options
    if (e.key === KeyCode.Space && delimiterRules === DelimiterRules.SpacesAreNotDelimiter) {
      e.preventDefault();
      setInputText(`${inputText} `);
    }
  };

  return (
    <label htmlFor={`multi-select-${labelId}`} className="input-label">
      {label}
      <AsyncSelect
        inputId={`multi-select-${labelId}`}
        isMulti
        loadOptions={loadOptions}
        defaultOptions
        getOptionValue={(option) => option.displayName}
        // Names options in react-select's screen-reader announcements, e.g. "North Ward, selected."
        getOptionLabel={(option) => option.displayName}
        placeholder={placeholder}
        classNames={{
          control: ({ isFocused }) => `!flex !p-0 input-default ${isFocused ? 'input-focused shadow-[#008849_0px_0px_0px_1px] border-transparent' : 'shadow-none'}`,
          option: ({ isSelected, isFocused }) => {
            const baseClasses = 'p-2';
            if (isFocused && isSelected) {
              return `${baseClasses} bg-primary font-semibold`;
            }
            if (isFocused) {
              return `${baseClasses} bg-pipbg-subtle`;
            }
            if (isSelected) {
              return `${baseClasses} bg-pipgrey-light font-semibold`;
            }
            return baseClasses;
          },
        }}
        styles={{ // keep basic default styles so not all custom
          control: () => ({}),
          option: () => ({}),
        }}
        menuPlacement={placement}
        hideSelectedOptions={false}
        value={selectedOptions}
        inputValue={inputText}
        onInputChange={onInputChange}
        onKeyDown={handleKeyDown}
        onChange={handleChange}
        components={{
          MultiValue: CustomMultiValue,
          ValueContainer: CustomValueContainer,
          Option: CustomOption,
          DropdownIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
      />
    </label>
  );
}
